import RestApiUtil from './RestApiUtil';
import AppsUtil from './AppsUtil';
import CCDUtil from './CCDUtil';
import ThorConnectionUtil from './ThorConnectionUtil';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import {getContentfulPath} from './contentfulDataPathUtil';
import {planProductsMap, productIdToSapCode, appButtonConfig, starterTab, installationTab, SophiaLocaleMap, SOPHIA_SURFACE_ID} from '../constants/appsConstants';
import {productsToRedirectFor} from '../constants/ccdConstants';
import Url from './Url';
import {ALL_APPS_APP_PICKER_QUERY_PARAM} from '../constants/AppPicker/appPickerConstants';
import getConfigApps from '../config';

function getFFCFallbackV5Object(type, isPlatformRequired) {
	return {
		type: 'url',
		url: RestApiUtil.getFFCFallbackUrl(type, isPlatformRequired)
	};
};

class FFCProductCallConfig {
	constructor(channel, withAppId, isCCD, sapCode) {
		this.channel = channel;
		this.headers = {
			withAuth: false,
			withAppId
		};
		this.config = sapCode ? {
			allVersion: true,
			sapCode
		} : {
			allVersion: false
		};
		this.isCCD = isCCD;
	}
}

export function getCriteriaServicesList(locale, environment) {
	const keyServiceMap = {};
	// set the environment
	window.ENV = environment;
	const servers = getConfigApps('servers');
	const isAllAppsStarter = (AppsUtil.getPathData().mainAppsTab === starterTab // isAllAppsPlanUrl
		&& AppsUtil.getPathData().product === 'all-apps')
		|| (AppsUtil.getCCWebPathData().isPDP // isAppPickerInstallUrl
		&& AppsUtil.getCCWebPathData().product
		&& Url.getParam('action') === 'install'
		&& Url.getParam('source') === ALL_APPS_APP_PICKER_QUERY_PARAM);
	if (AppsUtil.isLoggedOutExp()) {
		const productData = CCDUtil.getProductsData(AppsUtil.getLoggedOutExpPathData().product);
		if (!productData || productsToRedirectFor.indexOf(productData.id) !== -1) {
			return keyServiceMap;
		}
		const sapCode = productData.sapCode;
		keyServiceMap[`${KEYS_FOR_SERVICES.FFC_ALL}-${sapCode}-CCM`] = {
			...RestApiUtil.getFFCOptions(new FFCProductCallConfig('ccm', false, true, sapCode)),
			type: 'url',
			useCustomCatch: false
		};

		if (AppsUtil.isMobileOrTab() || DeviceDetectionUtil.isNotMacOrWin()) {
			keyServiceMap[KEYS_FOR_SERVICES.FFC_LATEST_FALLBACK_ALL_PLATFORM] = getFFCFallbackV5Object('latest', false);
		}
		else {
			keyServiceMap[KEYS_FOR_SERVICES.FFC_ALL_FALLBACK] = getFFCFallbackV5Object('all', true);
		}

		keyServiceMap[KEYS_FOR_SERVICES.FFC_FALLBACK_PRODUCT_JSON] = {
			type: 'url',
			url: `${servers.WAM_fallback}/${DeviceDetectionUtil.isWin() ? 'win' : 'mac'}/products.json`
		};
		const companionAppCode = AppsUtil.getCompanionApp(AppsUtil.getLoggedOutExpPathData().product);
		if (AppsUtil.isMobileOrTab() && companionAppCode && !AppsUtil.isLoggedOutCampaignExp) {
			// companion app ffc call
			keyServiceMap[`${KEYS_FOR_SERVICES.FFC_ALL}-${companionAppCode}-mobileApp`] = {
				...RestApiUtil.getFFCOptions(new FFCProductCallConfig('mobileApps', false, true, companionAppCode)),
				type: 'url',
				useCustomCatch: true
			};
		}
		keyServiceMap[KEYS_FOR_SERVICES.CHECK_FOR_THOR] = {
			type: 'promise',
			promise: ThorConnectionUtil.checkForThor()
		};
	}
	if (!AppsUtil.isLoggedOutExp() && !isAllAppsStarter) {
		keyServiceMap[KEYS_FOR_SERVICES.FFC_LATEST] = {
			...RestApiUtil.getFFCOptions(new FFCProductCallConfig('ccm,services,mobileApps', true, false)),
			type: 'url'
		};

		const contentfulSkeletonURL = `${AppsUtil.getCVSDomain()}${getContentfulPath('skeletonwithstock', locale)}`;
		if (AppsUtil.hasFeature('temp_test_render_sophia_latency')) {
			keyServiceMap[KEYS_FOR_SERVICES.GET_SOPHIA_ENDPOINTS] = {
				type: 'url',
				url: `${servers.SOPHIA}?surfaceId=${SOPHIA_SURFACE_ID.appSkeleton}&surfaceId=${SOPHIA_SURFACE_ID.allAppsBanners}&surfaceId=${SOPHIA_SURFACE_ID.categoriesBanners}&surfaceId=${SOPHIA_SURFACE_ID.pdpCatalog}&productLanguage=${SophiaLocaleMap[locale]}`,
				headers: RestApiUtil.getHeaders(true, false),
				callback: (dataFromSophia) => {
					let skeletonUrl = AppsUtil.fetchURLFromSophiaData(dataFromSophia, [SOPHIA_SURFACE_ID.appSkeleton])[SOPHIA_SURFACE_ID.appSkeleton];
					skeletonUrl = skeletonUrl || contentfulSkeletonURL;
					// return service key and skeleton URL
					return {
						key: KEYS_FOR_SERVICES.APP_SKELETON_SOPHIA,
						service: {
							type: 'url',
							url: skeletonUrl
						}
					};
				}
			};
		}

		keyServiceMap[KEYS_FOR_SERVICES.CHECK_FOR_THOR] = {
			type: 'promise',
			promise: ThorConnectionUtil.checkForThor()
		};

		// pre-fetch all versions data for starter pages
		const pathData = AppsUtil.getPathData();
		const pathDataCCWeb = AppsUtil.getCCWebPathData();
		let starterPlan = (pathData.mainAppsTab === starterTab || pathData.pdpTab === installationTab) && pathData.product ? pathData.product.toLowerCase() : '';
		if (pathDataCCWeb.isPDP && Url.getParam('action') === 'install') {
			starterPlan = pathDataCCWeb.product;
		}
		if (starterPlan && planProductsMap[starterPlan]) {
			const sapCodeKeys = Object.keys(planProductsMap[starterPlan]).filter(keyName => keyName.includes('ProductSapCode'));
			for (let i = 0; i < sapCodeKeys.length; i++) {
				const sapcode = planProductsMap[starterPlan][sapCodeKeys[i]];
				keyServiceMap[`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-${sapcode}`] = {
					type: 'promise',
					promise: RestApiUtil.fetchFFCAllVersionData(sapcode)
				};
			}
			if (starterPlan === 'ccpp' || starterPlan === 'ccppn') {
				// add phsp in case of activation test.
				keyServiceMap[`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-PHSP`] = {
					type: 'promise',
					promise: RestApiUtil.fetchFFCAllVersionData('PHSP')
				};
			}
		}

		if (starterPlan && productIdToSapCode[starterPlan] && appButtonConfig[productIdToSapCode[starterPlan]] && appButtonConfig[productIdToSapCode[starterPlan]].inPlan.pdp.indexOf('install') > -1) {
			keyServiceMap[`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-${productIdToSapCode[starterPlan]}`] = {
				type: 'promise',
				promise: RestApiUtil.fetchFFCAllVersionData(productIdToSapCode[starterPlan])
			};
		}

		keyServiceMap[KEYS_FOR_SERVICES.FFC_LATEST_FALLBACK] = getFFCFallbackV5Object('latest', true);

		keyServiceMap[KEYS_FOR_SERVICES.FFC_FALLBACK_PRODUCT_JSON] = {
			type: 'url',
			url: `${servers.WAM_fallback}/${DeviceDetectionUtil.isWin() ? 'win' : 'mac'}/products.json`
		};

		/**
		 * Commenting out for category revamp test
		 * keyServiceMap[KEYS_FOR_SERVICES.APP_SKELETON_FALLBACK] = {
		 *	type: 'url',
		 *	url: '/assets/apps/apps-skeleton/en/appsSkeletonRevamp.json'
		 * };
		**/
	}
	return keyServiceMap;
}

// PREFETCH
export const KEYS_FOR_SERVICES = {
	FFC_LATEST : 'cch-apps-ffc-latest',
	FFC_LATEST_FALLBACK : 'cch-apps-ffc-latest-fallback',
	FFC_FALLBACK_PRODUCT_JSON : 'cch-apps-ffc-fallback-product-json',
	APP_SKELETON : 'cch-apps-skeleton',
	APP_SKELETON_FALLBACK : 'cch-apps-skeleton-fallback',
	GET_SOPHIA_ENDPOINTS: 'cch-apps-get-endpoints-from-sophia',
	ALL_VERSION_FFC_DATA: 'cch-apps-ffc-all-version-apps-data',
	CHECK_FOR_THOR: 'cch-apps-check-for-thor', // nuj
	APP_SKELETON_SOPHIA: 'cch-apps-skeleton-from-sophia',
	FFC_ALL: 'cch-apps-ffc-all-version-data',
	FFC_ALL_FALLBACK: 'cch-apps-ffc-all-version-data-fallback',
	FFC_LATEST_FALLBACK_ALL_PLATFORM: 'cch-apps-ffc-latest-fallback-all-platform' // for mobile
};
